import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DollarSign, CheckCircle, XCircle, Clock, AlertTriangle, User, CreditCard } from "lucide-react";
import { format } from "date-fns";

interface RefundTicket {
  id: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  created_at: string;
  user_id: string;
  user_profile?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface CreditAdjustment {
  id: string;
  user_id: string;
  credit_change: number;
  adjustment_type: string;
  reason: string;
  previous_balance: number;
  new_balance: number;
  created_at: string;
  admin_id: string;
}

const priorityColors = {
  low: "bg-green-500/10 text-green-500",
  medium: "bg-yellow-500/10 text-yellow-500", 
  high: "bg-orange-500/10 text-orange-500",
  urgent: "bg-red-500/10 text-red-500"
};

const statusColors = {
  open: "bg-blue-500/10 text-blue-500",
  in_progress: "bg-yellow-500/10 text-yellow-500",
  resolved: "bg-green-500/10 text-green-500",
  closed: "bg-gray-500/10 text-gray-500"
};

export default function AdminRefundManager() {
  const [refundTickets, setRefundTickets] = useState<RefundTicket[]>([]);
  const [creditAdjustments, setCreditAdjustments] = useState<CreditAdjustment[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<RefundTicket | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Manual credit adjustment state
  const [showManualAdjustment, setShowManualAdjustment] = useState(false);
  const [manualUserId, setManualUserId] = useState("");
  const [manualUserEmail, setManualUserEmail] = useState("");
  const [manualCreditChange, setManualCreditChange] = useState<number>(0);
  const [manualReason, setManualReason] = useState("");
  const [manualType, setManualType] = useState("compassionate_refund");
  
  // Refund processing state
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [refundDecision, setRefundDecision] = useState<string>("");
  const [adminNotes, setAdminNotes] = useState("");
  
  const { toast } = useToast();

  useEffect(() => {
    fetchRefundTickets();
    fetchCreditAdjustments();
  }, []);

  const fetchRefundTickets = async () => {
    try {
      // First get the tickets
      const { data: tickets, error: ticketsError } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('category', 'refund_request')
        .order('created_at', { ascending: false });

      if (ticketsError) throw ticketsError;

      if (!tickets || tickets.length === 0) {
        setRefundTickets([]);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(tickets.map(t => t.user_id))];
      
      // Get user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, email, first_name, last_name')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Create a map of user profiles
      const profileMap = new Map();
      profiles?.forEach(profile => {
        profileMap.set(profile.user_id, profile);
      });

      // Combine tickets with profiles
      const ticketsWithProfiles = tickets.map(ticket => ({
        id: ticket.id,
        title: ticket.title,
        description: ticket.description,
        priority: ticket.priority,
        status: ticket.status,
        created_at: ticket.created_at,
        user_id: ticket.user_id,
        user_profile: profileMap.get(ticket.user_id) || null
      }));

      setRefundTickets(ticketsWithProfiles);
    } catch (error) {
      console.error('Error fetching refund tickets:', error);
      toast({
        title: "Error loading refund requests",
        description: "There was a problem loading refund requests.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditAdjustments = async () => {
    try {
      const { data, error } = await supabase
        .from('credit_adjustments')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setCreditAdjustments(data || []);
    } catch (error) {
      console.error('Error fetching credit adjustments:', error);
    }
  };

  const processRefundRequest = async () => {
    if (!selectedTicket || !refundDecision) return;

    try {
      setIsProcessing(true);

      await supabase.rpc('process_refund_request', {
        p_ticket_id: selectedTicket.id,
        p_refund_amount: refundAmount,
        p_refund_type: manualType,
        p_admin_decision: refundDecision,
        p_admin_notes: adminNotes
      });

      toast({
        title: "Refund processed",
        description: `Refund request ${refundDecision} successfully.`,
      });

      setSelectedTicket(null);
      setRefundAmount(0);
      setRefundDecision("");
      setAdminNotes("");
      fetchRefundTickets();
      fetchCreditAdjustments();
    } catch (error: any) {
      console.error('Error processing refund:', error);
      toast({
        title: "Error processing refund",
        description: error.message || "There was a problem processing the refund.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualCreditAdjustment = async () => {
    if (!manualUserId || manualCreditChange === 0 || !manualReason.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);

      await supabase.rpc('manual_credit_adjustment', {
        p_user_id: manualUserId,
        p_credit_change: manualCreditChange,
        p_adjustment_type: manualType,
        p_reason: manualReason
      });

      toast({
        title: "Credit adjustment completed",
        description: `Successfully adjusted credits by ${manualCreditChange > 0 ? '+' : ''}${manualCreditChange}.`,
      });

      setShowManualAdjustment(false);
      setManualUserId("");
      setManualUserEmail("");
      setManualCreditChange(0);
      setManualReason("");
      fetchCreditAdjustments();
    } catch (error: any) {
      console.error('Error adjusting credits:', error);
      toast({
        title: "Error adjusting credits",
        description: error.message || "There was a problem adjusting the credits.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const lookupUserByEmail = async () => {
    if (!manualUserEmail.trim()) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, email, first_name, last_name')
        .eq('email', manualUserEmail.trim())
        .single();

      if (error) throw error;

      if (data) {
        setManualUserId(data.user_id);
        toast({
          title: "User found",
          description: `Found: ${data.first_name} ${data.last_name} (${data.email})`,
        });
      }
    } catch (error) {
      console.error('Error looking up user:', error);
      toast({
        title: "User not found",
        description: "No user found with that email address.",
        variant: "destructive",
      });
    }
  };

  const getRefundStats = () => {
    const pending = refundTickets.filter(t => t.status === 'open').length;
    const approved = refundTickets.filter(t => t.status === 'resolved').length;
    const denied = refundTickets.filter(t => t.status === 'closed').length;
    return { pending, approved, denied, total: refundTickets.length };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading refund requests...</div>;
  }

  const stats = getRefundStats();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-energy">Refund Management</h2>
        <Button 
          onClick={() => setShowManualAdjustment(true)}
          className="energy-button"
        >
          Manual Credit Adjustment
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-energy">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Requests</div>
          </CardContent>
        </Card>
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-500">{stats.pending}</div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </CardContent>
        </Card>
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-500">{stats.approved}</div>
            <div className="text-sm text-muted-foreground">Approved</div>
          </CardContent>
        </Card>
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-500">{stats.denied}</div>
            <div className="text-sm text-muted-foreground">Denied</div>
          </CardContent>
        </Card>
      </div>

      {/* Refund Requests */}
      <Card className="obsidian-card">
        <CardHeader>
          <CardTitle className="text-energy flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Refund Requests
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {refundTickets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No refund requests found.
            </div>
          ) : (
            refundTickets.map((ticket) => (
              <Card key={ticket.id} className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-2 mb-2">
                        {ticket.priority === 'urgent' && (
                          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        )}
                        <h3 className="font-semibold">{ticket.title}</h3>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>
                          {ticket.user_profile?.first_name && ticket.user_profile?.last_name 
                            ? `${ticket.user_profile.first_name} ${ticket.user_profile.last_name}` 
                            : ticket.user_profile?.email || 'Unknown User'
                          }
                        </span>
                        <span>•</span>
                        <span>{format(new Date(ticket.created_at), 'MMM d, yyyy')}</span>
                      </div>

                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {ticket.description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        <Badge className={statusColors[ticket.status as keyof typeof statusColors]}>
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <Badge className={priorityColors[ticket.priority as keyof typeof priorityColors]}>
                          {ticket.priority.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => setSelectedTicket(ticket)}
                        disabled={ticket.status === 'resolved' || ticket.status === 'closed'}
                        size="sm"
                      >
                        {ticket.status === 'resolved' ? 'Approved' : 
                         ticket.status === 'closed' ? 'Denied' : 'Process'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Recent Credit Adjustments */}
      <Card className="obsidian-card">
        <CardHeader>
          <CardTitle className="text-energy flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Recent Credit Adjustments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {creditAdjustments.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No credit adjustments found.
              </div>
            ) : (
              creditAdjustments.map((adjustment) => (
                <div key={adjustment.id} className="flex items-center justify-between p-3 bg-muted/30 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold ${adjustment.credit_change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {adjustment.credit_change > 0 ? '+' : ''}{adjustment.credit_change} credits
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {adjustment.adjustment_type.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{adjustment.reason}</p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <div>{format(new Date(adjustment.created_at), 'MMM d, yyyy')}</div>
                    <div>Balance: {adjustment.previous_balance} → {adjustment.new_balance}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Refund Processing Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Process Refund Request</DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded">
                <h3 className="font-semibold mb-2">{selectedTicket.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  User: {selectedTicket.user_profile?.email}
                </p>
                <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="refundAmount">Refund Amount ($)</Label>
                  <Input
                    id="refundAmount"
                    type="number"
                    step="0.01"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <Label htmlFor="decision">Decision</Label>
                  <Select value={refundDecision} onValueChange={setRefundDecision}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select decision" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approve Refund</SelectItem>
                      <SelectItem value="denied">Deny Refund</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="adminNotes">Admin Notes</Label>
                <Textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedTicket(null)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={processRefundRequest}
                  disabled={isProcessing || !refundDecision}
                  className={refundDecision === 'approved' ? 'bg-green-600 hover:bg-green-700' : 
                           refundDecision === 'denied' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  {isProcessing ? 'Processing...' : 
                   refundDecision === 'approved' ? 'Approve & Process' : 
                   refundDecision === 'denied' ? 'Deny Request' : 'Process'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Credit Adjustment Dialog */}
      <Dialog open={showManualAdjustment} onOpenChange={setShowManualAdjustment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Credit Adjustment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter user email"
                value={manualUserEmail}
                onChange={(e) => setManualUserEmail(e.target.value)}
                className="flex-1"
              />
              <Button onClick={lookupUserByEmail} variant="outline">
                Lookup
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="creditChange">Credit Change</Label>
                <Input
                  id="creditChange"
                  type="number"
                  value={manualCreditChange}
                  onChange={(e) => setManualCreditChange(parseInt(e.target.value) || 0)}
                  placeholder="Enter + or - amount"
                />
              </div>
              
              <div>
                <Label htmlFor="adjustmentType">Adjustment Type</Label>
                <Select value={manualType} onValueChange={setManualType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compassionate_refund">Compassionate Refund</SelectItem>
                    <SelectItem value="technical_compensation">Technical Compensation</SelectItem>
                    <SelectItem value="admin_correction">Admin Correction</SelectItem>
                    <SelectItem value="promotional_credit">Promotional Credit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                placeholder="Explain the reason for this adjustment..."
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowManualAdjustment(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleManualCreditAdjustment}
                disabled={isProcessing || !manualUserId || manualCreditChange === 0 || !manualReason.trim()}
                className="energy-button"
              >
                {isProcessing ? 'Processing...' : 'Apply Adjustment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}